import { useRecoilState } from "recoil";
import { loginFormState } from "../../recoil/auth";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaUser, FaLock, FaTimes } from "react-icons/fa";
import styled from "styled-components";
import { useLogin } from "../../apis/api/Auth/useLogin";
import { useState, useEffect } from 'react';

function LoginForm() {
  const { register, handleSubmit, formState: { errors }, resetField } = useForm();
  const { t } = useTranslation();
  const loginMutation = useLogin();
  
  // Recoil 상태와 상태 업데이트 함수
  const [loginForm, setLoginForm] = useRecoilState(loginFormState);
  const [localRememberMe, setLocalRememberMe] = useState(loginForm.rememberMe);

  useEffect(() => {
    setLoginForm((prevForm) => ({
      ...prevForm,
      rememberMe: localRememberMe,
    }));
  }, [localRememberMe, setLoginForm]);

  // 로그인 폼 유효성 검사 및 제출 처리
  const onValid = (formData) => {
    const newFormData = {
      ...formData,
      rememberMe: localRememberMe,
    };

    // 로그인 요청 실행
    loginMutation.mutate(newFormData, {
      onSuccess: (data) => {
        if (localRememberMe) {
          setLoginForm({
            email: formData.email,
            password: formData.password,
            rememberMe: true
          });
        } else {
          setLoginForm({ email: '', password: '', rememberMe: false });
        }
      },
      onError: (error) => {
        console.error("Login failed:", error);
      },
    });
  };

  const clearInput = (inputName) => {
    resetField(inputName);
    setLoginForm({ ...loginForm, [inputName]: '' });
  };

  return (
    <FormContainer>
      <StyledForm onSubmit={handleSubmit(onValid)}>
        <InputWrapper>
          <IconWrapper>
            <FaUser />
          </IconWrapper>
          <InputWithIcon
            {...register("email", { required: `* ${t(`please enter email`)}` })}
            type="email"
            placeholder={t("Enter your email")}
            value={loginForm.email} // 값 동기화
            onInput={(e) => setLoginForm({ ...loginForm, email: e.target.value })} // 값 변경 시 상태 업데이트
          />
          {loginForm.email && (
            <ClearIcon onClick={() => clearInput("email")}>
              <FaTimes />
            </ClearIcon>
          )}
        </InputWrapper>
        <ErrorMsg>{errors?.email?.message}</ErrorMsg>

        <InputWrapper>
          <IconWrapper>
            <FaLock />
          </IconWrapper>
          <InputWithIcon
            {...register("password", { required: `* ${t(`please enter password`)}` })}
            type="password"
            placeholder={t("Enter your password")}
            value={loginForm.password} // 값 동기화
            onInput={(e) => setLoginForm({ ...loginForm, password: e.target.value })} // 값 변경 시 상태 업데이트
          />
          {loginForm.password && (
            <ClearIcon onClick={() => clearInput("password")}>
              <FaTimes />
            </ClearIcon>
          )}
        </InputWrapper>
        <ErrorMsg>{errors?.password?.message}</ErrorMsg>

        <RememberMeWrapper>
         <StyledCheckbox
            id="rememberMe"
            checked={localRememberMe}  // 로컬 상태 사용
            onChange={(e) => setLocalRememberMe(e.target.checked)}  // 로컬 rememberMe 상태 업데이트
          />
          <label htmlFor="rememberMe">{t("Remember Me")}</label>
        </RememberMeWrapper>
        <StyledButton type="submit">{t(`login`)}</StyledButton>
      </StyledForm>
    </FormContainer>
  );
}

// Styled Components
const FormContainer = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 12px 30px rgba(0, 0, 0, 0.1); 
  padding: 50px 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: sticky;
  z-index: 1;

  &:hover {
    box-shadow: 0px 16px 40px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 40px 20px;
  }

  @media (max-width: 480px) {
    padding: 30px 15px;
  }
`;

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background-color: #f7f9fc;
  border-radius: 8px;
  padding: 12px 28px;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.05);
  position: relative;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus-within {
    border-color: #007bff;
    box-shadow: 0 0 10px rgba(0, 123, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
  }

  @media (max-width: 480px) {
    padding: 8px 15px;
  }
`;

const IconWrapper = styled.div`
  color: #6c757d;
  font-size: 1.2rem;
  margin-right: 1.2rem;

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-right: 1rem;
  }
`;

const InputWithIcon = styled.input`
  width: 100%;
  padding: 14px;
  font-size: 1rem;
  border: none;
  background: transparent;
  outline: none;

  &::placeholder {
    color: #adb5bd;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 0.9rem;
  }
`;

const ClearIcon = styled.div`
  position: absolute;
  right: 8px;
  color: #adb5bd;
  cursor: pointer;
  font-size: 1.5rem;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: #ff6b6b;
    transform: scale(1.1);
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const ErrorMsg = styled.div`
  width: 100%;
  text-align: left;
  font-size: 0.85rem;
  color: #ff6b6b;

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const StyledButton = styled.button`
  width: 100%;
  padding: 14px;
  font-size: 1.1rem;
  font-weight: bold;
  color: #fff;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
  cursor: pointer;
  transition: background 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #0056b3, #003b7a);
    box-shadow: 0 6px 15px rgba(0, 123, 255, 0.3);
  }

  &:active {
    background-color: #003b7a;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 0.9rem;
  }
`;

const RememberMeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #6c757d;

  label {
    cursor: pointer;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;

    label {
      font-size: 0.85rem;
    }
  }
`;

const StyledCheckbox = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  width: 22px;
  height: 22px;
  border: 2px solid #adb5bd;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:checked {
    background-color: #007bff;
    border-color: #007bff;
  }

  &:checked::before {
    content: '✓';
    font-size: 14px;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:hover {
    border-color: #0056b3;
  }

  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
    font-size: 12px;
  }
`;

export default LoginForm;
